import { Stack } from '@mui/system';
import { getSelectedBoardId } from '@common-reducers/BoardsSelectors';
import { DBLogsThunks, logsSelectByQuery } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useEffect } from 'react';
import type { Log } from '@common-models/logs';
import AvatarGenerator from '../avatar-generator';
import CheckboxColumnLog from './task-logs-components/checkbox-column-log';
import DateColumnLog from './task-logs-components/date-column-log';
import DropdownColumnLog from './task-logs-components/dropdown-column-log';
import FilesColumnLog from './task-logs-components/files-column-column-log';
import LogCreation from './task-logs-components/log-creation';
import NewDeleteRestoreTaskLog from './task-logs-components/new-delete-task-log';
import NumberLinkTextPhoneColumnLog from './task-logs-components/number-link-text-phone-column-log';
import OptionStatusComplexityColumnLog from './task-logs-components/option-status-complexity-column-log';
import PersonColumnLog from './task-logs-components/person-column-log';
import RatingColumnLog from './task-logs-components/rating-column-log';
import RenameLog from './task-logs-components/rename-log';
import TimelineColumnLog from './task-logs-components/timeline-column-log';

export default function ExtraDataPanelLog() {

    const promiseList: unknown[] = [];
    const dispatch = useAppDispatch()
    const boardId = useAppSelector(state => getSelectedBoardId(state))
    const displayExtraDataOfTaskById = useAppSelector(
        (state) => state.ExtraDataReducer.displayExtraDataOfTaskById
    );

    useEffect(() => {
        console.log("get all board logs 2");
        promiseList.push(dispatch(
            DBLogsThunks.find({
                boardId,
                deleted: false,
                $paginate: false,
                $sort: {
                    createdAt: -1
                },
            })
        ))
    }, [])

    // Spread before sorting: selectByQuery returns a shared/cached array, so an
    // in-place .sort() on it would mutate the array seen by other readers.
    const taskLogs = [...useAppSelector((state) => logsSelectByQuery(state.DBLogReducer, { taskId: displayExtraDataOfTaskById }, [], {
        createdAt: -1
    }))]
        .sort((a, b) => {
            const aDate = a.createdAt ? new Date(a.createdAt) : new Date(0)
            const bDate = b.createdAt ? new Date(b.createdAt) : new Date(0)

            const aSeconds = Math.floor(aDate.getTime())
            const bSeconds = Math.floor(bDate.getTime())
            return bSeconds - aSeconds
        })

    const TaskLogData = (log: Log) => {
        const updateType = log.logUpdateType ?? '';
        const _id = log._id ?? '';
        if (
            ['new task', 'delete task', 'restore task']
                .includes(updateType) === true) {
            return (<NewDeleteRestoreTaskLog logId={_id} />)
        }
        if (
            ['task name']
                .includes(updateType) === true) {
            return (<RenameLog logId={_id} />)
        }
        if (
            ['option-picker', 'status-option-picker', 'complexity-picker']
                .includes(updateType) === true) {
            return (<OptionStatusComplexityColumnLog logId={_id} />)
        }
        if (
            ['person-picker']
                .includes(updateType) === true) {
            return (<PersonColumnLog logId={_id} />)
        }
        if (
            ['datepicker']
                .includes(updateType) === true) {
            return (<DateColumnLog logId={_id} />)
        }
        if (
            ['number', 'link', 'text-cell', 'phone-cell']
                .includes(updateType) === true) {
            return (<NumberLinkTextPhoneColumnLog logId={_id} />)
        }
        if (
            ['files']
                .includes(updateType) === true) {
            return (<FilesColumnLog logId={_id} />)
        }
        if (
            ['checkbox']
                .includes(updateType) === true) {
            return (<CheckboxColumnLog logId={_id} />)
        }
        if (
            ['rating']
                .includes(updateType) === true) {
            return (<RatingColumnLog logId={_id} />)
        }
        if (
            ['drop-down']
                .includes(updateType) === true) {
            return (<DropdownColumnLog logId={_id} />)
        }
        if (
            ['timeline-picker']
                .includes(updateType) === true) {
            return (<TimelineColumnLog logId={_id} />)
        }
    }

    const LogROw = (index: number) => {
        const log = taskLogs[index];
        if (!log) {
            return null;
        }
        if (index !== 1 || taskLogs[index]?.actionType !== 'restored') {
            return (
                <Stack direction='row' key={`${index}_logs_${displayExtraDataOfTaskById}`} gap={1}
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: "white",
                        padding: "5px 24px 5px 16px",
                        minHeight: "80px",
                        borderBottom: index === taskLogs.length - 1 ? 'none' : '1px solid #DEEEF2',
                    }}>
                    <Stack direction='row'>
                        <AvatarGenerator itemId={log?._id ?? ''} size={32} isUser={true} />&nbsp;&nbsp;
                        {TaskLogData(log)}
                    </Stack>
                    <div style={{ width: '50px', }}><LogCreation logId={log?._id ?? ''} /></div>
                </Stack>
            )
        }
    }

    return (
        <Stack direction='column'
            sx={{
                borderBottom: '1px solid #DEEEF2',
                borderLeft: '1px solid #DEEEF2',
                borderRadius: "8px",
                overflow: "hidden",
            }}>
            {
                taskLogs.map((log, index) => {
                    return (LogROw(index))
                })
            }
        </Stack>
    )
}

/// Comments:
/// text logs needs to have the css fixed inside cellWrapper component.
/// files column has a problem with deleting the last file. Also, its not logged.
/// number, text, phone columns creates double logs